import type { RequiredVisit, VisitVerification, VisitVerdict } from '../types';

/** Site-detail verification grid: one row per RequiredVisit, one column
 *  per day in the given week.  Each cell shows the day's verdict as a
 *  colored chip.  Sprint 3.
 */

const VERDICT_STYLES: Record<VisitVerdict, string> = {
  verified: 'bg-emerald-500 text-white',
  partial: 'bg-amber-400 text-slate-900',
  unverified: 'bg-red-500 text-white',
  excused: 'bg-sky-400 text-white',
  unknown: 'bg-slate-300 text-slate-700',
};

const VERDICT_SYMBOL: Record<VisitVerdict, string> = {
  verified: 'V', partial: 'P', unverified: 'X', excused: 'E', unknown: '?',
};

function fmtDay(iso: string) {
  // Format an ISO date string as "Mon 3/2" without ever routing through
  // a Date object built from a timezone-local parse — otherwise browser
  // tz shifts the day back into UTC and the header label drifts.
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    weekday: 'short', month: 'numeric', day: 'numeric', timeZone: 'UTC',
  });
}

function addDaysIso(iso: string, delta: number): string {
  // Pure string+UTC arithmetic — no local Date parse, so the value is
  // stable across browser timezones.  Bug guard: earlier the grid built
  // `new Date('YYYY-MM-DDT00:00:00')` (parsed as local), then called
  // `.toISOString().slice(0,10)` which shifted eastward viewers into
  // the previous UTC day and no verdict key ever matched.
  const [y, m, d] = iso.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + delta);
  return utc.toISOString().slice(0, 10);
}

interface Props {
  weekStart: string;           // 'YYYY-MM-DD'
  visits: RequiredVisit[];
  verifications: VisitVerification[];
}

export default function VerificationGrid({ weekStart, visits, verifications }: Props) {
  const days = Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i));

  // Index verifications by (visit_id, day)
  const byKey = new Map<string, VisitVerification>();
  for (const v of verifications) {
    byKey.set(`${v.required_visit}|${v.occurrence_date}`, v);
  }

  // Sort visits by display_order for a stable grid.
  const orderedVisits = [...visits].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  if (orderedVisits.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic">
        No required visits defined yet — nothing to verify.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="pr-3 py-1 font-medium min-w-[10rem]">Visit</th>
            {days.map((iso) => (
              <th key={iso} className="px-1 py-1 text-center font-medium">
                {fmtDay(iso)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orderedVisits.map((v) => (
            <tr key={v.id} className="border-t border-slate-100">
              <td className="pr-3 py-1 text-slate-800">
                <div className="font-medium">
                  {v.label || v.visit_type.replace(/_/g, ' ')}
                </div>
                <div className="text-[11px] text-slate-500 capitalize">
                  {v.criticality}
                </div>
              </td>
              {days.map((iso) => {
                const key = `${v.id}|${iso}`;
                const verif = byKey.get(key);
                if (!verif) {
                  return (
                    <td key={key} className="px-1 py-1 text-center">
                      <span
                        className="inline-block w-7 h-7 rounded bg-slate-100 text-slate-400 leading-7"
                        title="No verdict"
                      >—</span>
                    </td>
                  );
                }
                const title = [
                  `Verdict: ${verif.verdict}`,
                  `Matched ${verif.matched_event_count}/${verif.expected_event_count}`,
                  verif.reason && `Reason: ${verif.reason}`,
                  verif.excused_by_label && `Excused by: ${verif.excused_by_label}`,
                  verif.partial_reasons.length > 0 && `Partial: ${verif.partial_reasons.join(', ')}`,
                  !verif.plan_was_acknowledged && '(plan not acknowledged by vendor)',
                ].filter(Boolean).join(' • ');
                return (
                  <td key={key} className="px-1 py-1 text-center">
                    <span
                      className={`inline-block w-7 h-7 rounded font-semibold leading-7 ${VERDICT_STYLES[verif.verdict]}`}
                      title={title}
                    >
                      {VERDICT_SYMBOL[verif.verdict]}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
        {(['verified', 'partial', 'unverified', 'excused', 'unknown'] as VisitVerdict[]).map((v) => (
          <div key={v} className="flex items-center gap-1 capitalize">
            <span className={`inline-block w-3 h-3 rounded ${VERDICT_STYLES[v]}`} />
            {v}
          </div>
        ))}
      </div>
    </div>
  );
}
